<template>
    <slot />
    <Teleport to="body">
        <pbl-dialog
            ref="dialogRef"
            :headline="t('transformers.conditionStatus.content.editNextReportDateModal.title')"
            click-shield
            class="edit-next-report-date-dialog"
        >
            <div class="edit-next-report-date-modal">
                <div class="edit-next-report-date-modal__detail-line">
                    <VeeFormInputField
                        name="nextReportDate"
                        :label="t('transformers.conditionStatus.content.editNextReportDateModal.labels.nextReportDate')"
                        type="date"
                    />
                </div>
            </div>
            <pbl-button
                slot="footer"
                variant="secondary"
                @click="closeModal"
            >
                {{ t('common.cancel') }}
            </pbl-button>
            <pbl-button
                slot="footer"
                :disabled="!meta.valid || !meta.dirty || isMutating"
                :loading="isMutating"
                @click="onSubmit"
            >
                {{ t('common.confirm') }}
            </pbl-button>
        </pbl-dialog>
    </Teleport>
</template>

<script setup lang="ts">
import { ref, toRefs } from 'vue'
import { PebbleDialog } from '@pebble/core'
import { useI18n } from 'vue-i18n'
import { useForm } from 'vee-validate'
import { AxiosError } from 'axios'
import { usePebbleToasts } from '@/composables/pebble/usePebbleToasts'
import { EditNextReportDateModalPayload, provideEditNextReportDateModal } from '@/components/modals/edit-next-report-date/useEditNextReportDateModal'
import { editNextReportDateValidationSchema } from '@/components/modals/edit-next-report-date/EditNextReportDateModal.form'
import VeeFormInputField from '@/components/form-inputs/vee-wrapped-form-inputs/VeeFormInputField.vue'
import { format } from 'date-fns'
import { useEditNextReportDateMutation } from '@/api/mutations/latest-condition-reports/useEditNextReportDateMutation'
import { DATE_FORMAT_API } from '@/utils/date-utils'

const { t } = useI18n()
const toasts = usePebbleToasts()

const props = defineProps<{
    componentId: string,
}>()

const { componentId } = toRefs(props)

const dialogRef = ref<PebbleDialog | null>(null)
const contextPayload = ref<EditNextReportDateModalPayload | undefined>()

const { mutateAsync, isPending: isMutating } = useEditNextReportDateMutation()

const { handleSubmit, resetForm, meta } = useForm({
    validationSchema: editNextReportDateValidationSchema,
    initialValues: {
        nextReportDate: undefined,
    },
})

const openModal = (payload: EditNextReportDateModalPayload) => {
    contextPayload.value = payload

    resetForm({
        values: {
            nextReportDate: payload.nextReportDate ? format(new Date(payload.nextReportDate), DATE_FORMAT_API) : undefined,
        },
    })

    dialogRef.value?.show()
}

const closeModal = () => {
    dialogRef.value?.hide()
}

const onSubmit = handleSubmit(async (values) => {
    if (!contextPayload.value) {
        return
    }

    try {
        await mutateAsync({
            componentSid: componentId.value,
            reportType: contextPayload.value.reportType,
            reportId: contextPayload.value.reportId,
            date: values.nextReportDate,
        })

        closeModal()
    } catch (err) {
        if (err instanceof AxiosError) {
            toasts?.error({
                message: t('common.errors.unknown'),
                duration: 3000,
            })
        }
    }
})

provideEditNextReportDateModal({
    openModal,
})
</script>

<style scoped lang="scss">
.edit-next-report-date-modal {
    display: flex;
    flex-direction: column;

    gap: $spacing-m;

    &__detail-line {
        display: grid;
        grid-template-columns: 1fr 1fr;
        column-gap: $spacing-m;
    }
}

.edit-next-report-date-dialog {
    --width: 50vw;
}
</style>
